import React from "react";
import { Search, Palette, Hand, Smile, RefreshCw, Check, X } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle, PageHero } from "../components/site/Primitives";
import LeadForm from "../components/LeadForm";
import SEO from "../components/SEO";

const STEPS = [
  { icon: Search, title: "Avaliação", desc: "Entendemos o nível de calvície, estilo desejado, rotina e expectativas do cliente." },
  { icon: Palette, title: "Escolha da prótese", desc: "Escolhemos tonalidade, densidade, tamanho e tipo de cabelo." },
  { icon: Hand, title: "Aplicação", desc: "A prótese é personalizada, adaptada e cortada para o cliente." },
  { icon: Smile, title: "Resultado", desc: "O cliente sai com o novo visual no mesmo atendimento." },
  { icon: RefreshCw, title: "Manutenção", desc: "Orientamos sobre cuidados e realizamos manutenções periódicas." },
];

const CHOICES = ["Cor", "Densidade", "Modelo", "Tipo de cabelo", "Tamanho", "Corte", "Acabamento"];
const NOT_TRANSPLANT = [
  "Não envolve cirurgia", "O resultado é imediato", "É possível escolher o estilo",
  "É possível escolher o tipo de cabelo", "Exige manutenção periódica", "Pode atender diferentes níveis de calvície",
];
const TYPES = ["Cabelo liso", "Ondulado", "Cacheado", "Afro", "Corte social", "Corte moderno", "Cabelo curto", "Cabelo médio"];

export default function ProteseCapilar() {
  const { site } = useSite();
  const img = site?.images || {};

  return (
    <div>
      <SEO title="Prótese Capilar Masculina | Prime Capilar Clinic" description="Prótese capilar masculina sem cirurgia e com resultado imediato em Arapiraca, Garanhuns e Maceió. Solicite sua avaliação." />
      <PageHero
        overline="Prime Capilar Clinic"
        title={<>RECUPERE O CABELO.<br /><span className="text-gold">RECUPERE SUA CONFIANÇA.</span></>}
        subtitle="A prótese capilar é uma solução estética para homens que desejam recuperar o visual dos cabelos sem cirurgia e com resultado imediato."
        image={img.clinic_scalp}
        logo={site?.logo_prime}
      >
        <a href="#avaliacao" className="inline-flex items-center justify-center gap-2 font-heading font-600 tracking-wide rounded-sm px-7 py-4 bg-gold hover:bg-gold-hover text-black transition-colors" data-testid="protese-hero-avaliacao">QUERO FAZER UMA AVALIAÇÃO</a>
      </PageHero>

      {/* Choices */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <Overline className="mb-3">Personalização total</Overline>
              <SectionTitle>Cada cliente passa por uma avaliação</SectionTitle>
              <p className="mt-4 text-zinc-400 leading-relaxed">Para escolher com precisão cada detalhe do seu novo visual:</p>
              <div className="flex flex-wrap gap-2.5 mt-6">
                {CHOICES.map((c) => (
                  <span key={c} className="border border-white/15 rounded-sm px-4 py-2 text-sm text-zinc-200">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-white/10">
              <img src={img.styling2} alt="Prótese capilar" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#080808] border-y border-white/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><Overline className="mb-3">Como funciona</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle>Cinco etapas até o seu novo visual</SectionTitle></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border border-white/10 rounded-sm p-6 h-full bg-[#0d0d0d] hover:border-gold/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <s.icon className="w-6 h-6 text-gold" />
                    <span className="font-heading font-700 text-3xl text-white/10">{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-600 uppercase text-white text-base mt-4">{s.title}</h3>
                  <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Not transplant */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div>
              <Overline className="mb-3">Entenda a diferença</Overline>
              <SectionTitle>Prótese capilar não é transplante</SectionTitle>
              <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">A prótese capilar é uma solução estética, não cirúrgica, com resultado no mesmo dia.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {NOT_TRANSPLANT.map((t, i) => (
                <div key={i} className="flex items-start gap-2.5 border border-white/10 rounded-sm px-4 py-3.5 bg-[#0d0d0d]">
                  {t.startsWith("Não envolve") ? <X className="w-4 h-4 text-gold mt-0.5 shrink-0" /> : <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />}
                  <span className="text-sm text-zinc-200">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Types */}
      <section className="bg-[#080808] border-y border-white/10 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><Overline className="mb-3">Tipos de resultados</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle>Diferentes possibilidades para o seu estilo</SectionTitle></Reveal>
          <div className="flex flex-wrap gap-3 mt-8">
            {TYPES.map((t) => (
              <span key={t} className="border border-white/15 rounded-sm px-5 py-2.5 text-sm text-zinc-200 hover:border-gold hover:text-gold transition-colors">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="avaliacao" className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div>
              <Overline className="mb-3">Avaliação gratuita</Overline>
              <SectionTitle>Quero fazer uma avaliação</SectionTitle>
              <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
                Preencha os dados abaixo. Nosso time entra em contato pelo WhatsApp para indicar a melhor opção para o seu caso.
              </p>
              <p className="mt-4 text-sm text-zinc-500 max-w-md">
                O valor depende do modelo, tamanho, tipo de cabelo e necessidade de cada cliente. Por isso, primeiro realizamos uma avaliação.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-white/12 rounded-sm bg-[#0b0b0b] p-6 lg:p-8">
              <LeadForm paginaOrigem="Prótese Capilar" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
